import React, { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { useGetServiceCostPrices, useGetTourServices } from '~/hooks/apis/tours/useTourCostPrices';

import { FixedCostPriceItem, SlidingScalePricingItem } from '~/types/tours/costPrice';

import FixedPriceDialog from './FixedPriceDialog';
import FixedPriceTable from './FixedPriceTable';
import SlidingScaleTable from './SlidingScaleTable';
import SlidingScaleUploadDialog from './SlidingScaleUploadDialog';

interface Props {
  tourId: string;
}

const SERVICE_ID_PARAM = 'serviceId';

export default function TourSlidingScale({ tourId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isFixedPriceOpen, setIsFixedPriceOpen] = useState(false);

  const { data: services = [], isLoading: isLoadingServices, error: servicesError } = useGetTourServices(tourId);

  /**
   * The chosen service is kept in the URL so the view can be linked and
   * survives a reload. It defaults to the tour's only service when there is
   * just one, which is the common case.
   */
  const selectedServiceId = searchParams.get(SERVICE_ID_PARAM) ?? (services.length === 1 ? services[0].id : '');

  const handleServiceChange = useCallback(
    (serviceId: string) => {
      const next = new URLSearchParams(searchParams);

      if (serviceId) {
        next.set(SERVICE_ID_PARAM, serviceId);
      } else {
        next.delete(SERVICE_ID_PARAM);
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) ?? null,
    [services, selectedServiceId],
  );

  // Cost prices are keyed by supplier and service, both taken from the
  // selected service.
  const serviceIdentifier = useMemo(
    () => ({ supplierId: selectedService?.supplierId, serviceId: selectedService?.id }),
    [selectedService],
  );

  const { data, isLoading, error } = useGetServiceCostPrices(serviceIdentifier);

  const pricing = useMemo<Array<SlidingScalePricingItem>>(() => data?.slidingScale ?? [], [data]);
  const fixedPrices = useMemo<Array<FixedCostPriceItem>>(() => data?.fixed ?? [], [data]);

  // Fixed prices are quoted in the same currency as the rest of the service, so
  // an existing sliding scale is the best available default.
  const supplierCurrency = pricing[0]?.currency;

  // Offer the seasons the service already prices in, so a fixed price does not
  // introduce a near duplicate like "low" alongside "Low".
  const seasonOptions = useMemo(() => Array.from(new Set(pricing.map((item) => item.season))).sort(), [pricing]);

  /**
   * Package option and currency pairs already holding a sliding scale, so the
   * fixed price dialog can warn that saving replaces the seasons it sets.
   */
  const slidingScalePairs = useMemo(() => {
    if (!selectedService) {
      return [];
    }

    return pricing.reduce<Array<string>>((pairs, item) => {
      const configuration = selectedService.configurations.find(
        (candidate) => candidate.name === item.tourConfiguration,
      );
      const packageConfiguration = configuration?.packages.find(
        (candidate) => candidate.name === item.packageConfiguration,
      );

      return packageConfiguration ? pairs.concat(`${packageConfiguration.id}::${item.currency}`) : pairs;
    }, []);
  }, [pricing, selectedService]);

  return (
    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
        {/* The admin theme renders every heading variant at normal weight, and
            h5/h6 at body size, so size and weight are set explicitly here. */}
        <Typography variant="h2" fontWeight={600}>
          Sliding scale
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => setIsUploadOpen(true)} disabled={!selectedService}>
            {pricing.length ? 'Re-upload sliding scale' : 'Upload sliding scale'}
          </Button>
          <Button variant="text" onClick={() => setIsFixedPriceOpen(true)} disabled={!selectedService}>
            Set fixed price
          </Button>
        </Stack>
      </Stack>

      {isLoadingServices && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!servicesError && <Alert severity="error">{servicesError.message}</Alert>}

      {!isLoadingServices && !servicesError && !services.length && (
        <Alert severity="info">
          This tour has no supplier services yet, so there is nowhere to attach a cost price. Create a supplier and a
          service for the tour first.
        </Alert>
      )}

      {!!services.length && (
        <TextField
          select
          label="Service"
          value={selectedServiceId}
          onChange={(event) => handleServiceChange(event.target.value)}
          helperText="Cost prices belong to a supplier's service on this tour"
          sx={{ maxWidth: 560 }}
        >
          {services.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {service.name} — {service.kind}
            </MenuItem>
          ))}
        </TextField>
      )}

      {!!selectedService && isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!!selectedService && !!error && <Alert severity="error">{error.message}</Alert>}

      {!!selectedService && !isLoading && !error && (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={600}>
            Cost prices
          </Typography>
          <SlidingScaleTable pricing={pricing} />
          <FixedPriceTable prices={fixedPrices} />
        </Stack>
      )}

      {isUploadOpen && !!selectedService && (
        <SlidingScaleUploadDialog
          open={isUploadOpen}
          service={selectedService}
          existingRowCount={pricing.length}
          onClose={() => setIsUploadOpen(false)}
        />
      )}

      {isFixedPriceOpen && !!selectedService && (
        <FixedPriceDialog
          open={isFixedPriceOpen}
          service={selectedService}
          defaultCurrency={supplierCurrency}
          seasonOptions={seasonOptions}
          slidingScalePairs={slidingScalePairs}
          onClose={() => setIsFixedPriceOpen(false)}
        />
      )}
    </Stack>
  );
}
